import { articles } from "@/mocks/articles";
import { products, getProductUrl } from "@/mocks/products";

type Lang = "en" | "pt" | "de";
type ArticleItem = (typeof articles)["en"][number];

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  ogType: string;
  ogImage?: string;
  robots?: string;
  jsonLd?: Record<string, unknown>[];
}

const FAVICON_URL =
  "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png";

const HOME_HERO_IMAGE =
  "https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20of%20colorful%20sneakers%20hoodies%20caps%20and%20bags%20neatly%20arranged%20on%20a%20soft%20sage%20green%20background%20with%20a%20laptop%20showing%20a%20spreadsheet%20bright%20editorial%20photography%20vivid%20accent%20colors%20clean%20modern%20aesthetic&width=1600&height=900&seq=hero-001&orientation=landscape&nocache=true";

export function siteOrigin(): string {
  const value = import.meta.env.VITE_SITE_URL as string | undefined;
  return value ? value.replace(/\/+$/, "") : "";
}

function langOf(pathname: string): Lang {
  if (pathname.startsWith("/pt")) return "pt";
  if (pathname.startsWith("/de")) return "de";
  return "en";
}

interface PageSeo {
  title: string;
  description: string;
  keywords: string;
}

const HOME_SEO: Record<Lang, PageSeo> = {
  en: {
    title:
      "CSSBuy Spreadsheet 2026 — Find, Compare & Organize Products | cssbuybr.link",
    description:
      "Explore CSSBuy Spreadsheet — an independent resource for organized product links, categories, buying guides, QC information and shipping resources. Find products faster and easier.",
    keywords:
      "CSSBuy Spreadsheet, CSSBuy review, CSSBuy shipping cost, CSSBuy coupon, CSSBuy discount code, CSSBuy warehouse, CSSBuy legit, CSSBuy QC, CSSBuy products, CSSBuy buying guide",
  },
  pt: {
    title:
      "CSSBuy Spreadsheet 2026 — Encontre, Compare e Organize Produtos | cssbuybr.link",
    description:
      "Explore o CSSBuy Spreadsheet — um recurso independente com links de produtos, categorias, guias de compra, informações de QC e recursos de envio organizados.",
    keywords:
      "CSSBuy Spreadsheet, CSSBuy review, CSSBuy custo de envio, CSSBuy cupom, CSSBuy desconto, CSSBuy legit, CSSBuy QC, CSSBuy produtos",
  },
  de: {
    title:
      "CSSBuy Spreadsheet 2026 — Produkte finden, vergleichen & organisieren | cssbuybr.link",
    description:
      "Entdecken Sie CSSBuy Spreadsheet — eine unabhängige Ressource mit organisierten Produktlinks, Kategorien, Kaufratgebern, QC-Informationen und Versandressourcen.",
    keywords:
      "CSSBuy Spreadsheet, CSSBuy Review, CSSBuy Versandkosten, CSSBuy Gutschein, CSSBuy Rabattcode, CSSBuy legit, CSSBuy QC, CSSBuy Produkte",
  },
};

const PRODUCTS_SEO: Record<Lang, PageSeo> = {
  en: {
    title: "CSSBuy Product Directory — Browse All Products | cssbuybr.link",
    description:
      "Browse every product from the CSSBuy Spreadsheet directory — shoes, hoodies, t-shirts, jackets, bags and more. Click any item to view and buy on the main site.",
    keywords:
      "CSSBuy products, CSSBuy directory, CSSBuy Spreadsheet, CSSBuy categories, CSSBuy links, CSSBuy finds",
  },
  pt: {
    title:
      "Diretório de Produtos CSSBuy — Navegue por Todos os Produtos | cssbuybr.link",
    description:
      "Navegue por todos os produtos do diretório CSSBuy Spreadsheet — sapatos, hoodies, camisetas, jaquetas, bolsas e mais. Clique em qualquer item para ver e comprar no site principal.",
    keywords:
      "produtos CSSBuy, diretório CSSBuy, CSSBuy Spreadsheet, categorias CSSBuy, links CSSBuy",
  },
  de: {
    title: "CSSBuy Produktverzeichnis — Alle Produkte durchsuchen | cssbuybr.link",
    description:
      "Durchsuchen Sie alle Produkte des CSSBuy Spreadsheet-Verzeichnisses — Schuhe, Hoodies, T-Shirts, Jacken, Taschen und mehr. Klicken Sie auf ein Produkt, um es auf der Hauptseite zu kaufen.",
    keywords:
      "CSSBuy Produkte, CSSBuy Verzeichnis, CSSBuy Spreadsheet, CSSBuy Kategorien, CSSBuy Links",
  },
};

function absUrl(origin: string, path: string): string {
  return origin ? `${origin}${path}` : path;
}

function buildHomeJsonLd(
  origin: string,
  lang: Lang,
  seo: PageSeo
): Record<string, unknown>[] {
  const url = absUrl(origin, "/");
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "CSSBuy Spreadsheet",
      url,
      description: seo.description,
      inLanguage: lang === "pt" ? "pt-BR" : lang,
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "CSSBuy Spreadsheet",
      url,
      logo: { "@type": "ImageObject", url: FAVICON_URL },
    },
  ];
}

function buildArticleJsonLd(
  origin: string,
  pathname: string,
  article: ArticleItem
): Record<string, unknown>[] {
  const url = absUrl(origin, pathname);
  return [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.metaDescription,
      image: article.heroImage,
      author: { "@type": "Organization", name: article.author },
      publisher: {
        "@type": "Organization",
        name: "CSSBuy Spreadsheet",
        logo: { "@type": "ImageObject", url: FAVICON_URL },
      },
      datePublished: article.date,
      dateModified: article.updated,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: absUrl(origin, "/"),
        },
        { "@type": "ListItem", position: 2, name: article.category, item: url },
      ],
    },
  ];
}

function buildProductsJsonLd(
  origin: string,
  pathname: string,
  lang: Lang
): Record<string, unknown>[] {
  const url = absUrl(origin, pathname);
  const itemListElement = products.map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: p.name,
    url: getProductUrl(p.id),
  }));
  return [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "CSSBuy Product Directory",
      url,
      description: PRODUCTS_SEO[lang].description,
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: itemListElement.length,
        itemListElement,
      },
    },
  ];
}

export function resolveSeoData(pathname: string): SeoData {
  const lang = langOf(pathname);
  const origin = siteOrigin();

  // Home (default English is "/", plus /en, /pt, /de)
  if (
    pathname === "/" ||
    pathname === "/en" ||
    pathname === "/pt" ||
    pathname === "/de"
  ) {
    const seo = HOME_SEO[lang];
    return {
      title: seo.title,
      description: seo.description,
      keywords: seo.keywords,
      canonicalPath: pathname === "/en" ? "/" : pathname,
      ogType: "website",
      ogImage: HOME_HERO_IMAGE,
      jsonLd: buildHomeJsonLd(origin, lang, seo),
    };
  }

  // Article detail pages
  const articleMatch = pathname.match(/^(?:\/(?:pt|de|en))?\/article\/([^/]+)$/);
  if (articleMatch) {
    const slug = articleMatch[1];
    const list = articles[lang] || articles.en;
    const article = list.find((a) => a.slug === slug);
    if (article) {
      return {
        title: article.metaTitle,
        description: article.metaDescription,
        keywords: `CSSBuy Spreadsheet, ${article.category}, ${article.title}`,
        canonicalPath: pathname,
        ogType: "article",
        ogImage: article.heroImage,
        jsonLd: buildArticleJsonLd(origin, pathname, article),
      };
    }
  }

  // Products directory
  if (/^(?:\/(?:pt|de|en))?\/products$/.test(pathname)) {
    const seo = PRODUCTS_SEO[lang];
    return {
      title: seo.title,
      description: seo.description,
      keywords: seo.keywords,
      canonicalPath: pathname,
      ogType: "website",
      ogImage: HOME_HERO_IMAGE,
      jsonLd: buildProductsJsonLd(origin, pathname, lang),
    };
  }

  // Fallback (404 / unknown) — non-indexable
  return {
    title: "Page Not Found | cssbuybr.link",
    description: "This page could not be found.",
    keywords: "CSSBuy Spreadsheet",
    canonicalPath: pathname,
    ogType: "website",
    robots: "noindex, nofollow",
  };
}